import React, { useState, useEffect, useCallback } from 'react';

import { CartShippingModel } from '@/models';
import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { formatLongLocalDate } from '@/utils/dateUtils';

import CheckboxV2 from '@/components/common/CheckboxV2';
import SelectDropdown from '@/components/common/SelectDropdown';

const dateOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
const getEmptyOption = (text) => ({ value: '', label: text });

const dateComparator = (inputDate) => (date) => {
  const d = new Date(date.start);
  d.setHours(0, 0, 0, 0);
  return d.getTime() === (new Date(inputDate)).getTime();
};

type DateValueLabel = {
  value: Date;
  label: string;
}

type DriveDateSelectorProps = {
  t: (k: string, o?: any) => string;
  className?: string
  onChange: (e) => void;
  shippingId?: string;
  isExproCheckout: boolean;
}
export const DriveDateSelector = (props: DriveDateSelectorProps): React.JSX.Element => {
  const { t, className, onChange, shippingId, isExproCheckout } = props;

  const storeConf = useAppSelector((({ root }) => root.storeConf));
  const cartShippingMode = useAppSelector(({ cart }) => cart.content.shippingMode || {} as CartShippingModel);
  const exproShippingMode = useAppSelector(({ expro }) => expro.exproOrder.shippingMode || {} as CartShippingModel);

  const { shippingService } = useServices();

  const locale = storeConf.codeISO.toLowerCase();

  const [pickUpLater, setPickUpLater] = useState(
    isExproCheckout ? exproShippingMode.pickUpLater ?? false : cartShippingMode.pickUpLater ?? false
  );
  const [bookingSlot, setBookinSlot] = useState(isExproCheckout ? exproShippingMode.bookingSlot : cartShippingMode.bookingSlot);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableHours, setAvailableHours] = useState([]);
  const [selectedDay, setSelectedDay] = useState({} as DateValueLabel);
  const [selectedHour, setSelectedHour] = useState({} as DateValueLabel);

  const getSelectedSlot = useCallback(({ value }) => availableSlots.find(({ start }) => (
    (new Date(start)).getTime() === (new Date(value)).getTime()
  )), [availableSlots]);

  const handlePickUpLater = useCallback((selected) => {
    let mode;
    if (selected) {
      mode = { pickUpLater: true };
    } else if (selectedHour?.value) {
      mode = getSelectedSlot(selectedHour);
    }
    onChange(mode);
  }, [pickUpLater, selectedHour, onChange]);

  const mapSlotToHour = ({ start }) => ({
    value: start,
    label: (new Date(start)).toLocaleTimeString(locale, { hour: 'numeric', minute: 'numeric' })
  });

  useEffect(() => {
    async function fillState() {
      let slots = [];

      try {
        slots = await shippingService.getAvailableSlots(shippingId);
      } catch (e) {
        console.error(e);
      }

      // Group slots by date by setting time to 00:00:00
      const dates = slots.reduce((acc, slot) => {
        const startDate = new Date(slot.start);
        startDate.setHours(0, 0, 0, 0);

        const formattedDate = formatLongLocalDate(startDate, locale, dateOptions);

        if (!acc.find(({ value }) => value.getTime() === startDate.getTime())) {
          acc.push({
            value: startDate,
            label: formattedDate
          });
        }

        return acc;
      }, []);

      dates.unshift(getEmptyOption(t('checkout:shipMode.pickUpDate')));

      setAvailableDates(dates);
      setAvailableSlots(slots);

      if (bookingSlot?.start) {
        const startDate = new Date(bookingSlot.start);
        startDate.setHours(0, 0, 0, 0);

        const formattedDate = formatLongLocalDate(startDate, locale, dateOptions);
        const formattedHour = (new Date(bookingSlot.start)).toLocaleTimeString(locale, { hour: 'numeric', minute: 'numeric' });
        const hours = slots.filter(dateComparator(startDate)).map(mapSlotToHour);
        hours.unshift(getEmptyOption(t('checkout:shipMode.pickUpHour')));

        setSelectedDay({ value: startDate, label: formattedDate });
        setSelectedHour({ value: bookingSlot.start, label: formattedHour });
        setAvailableHours(hours);
      }
    }

    fillState();
  }, []);

  useEffect(() => {
    if (isExproCheckout) {
      setPickUpLater(exproShippingMode.pickUpLater ?? false);
      setBookinSlot(exproShippingMode.bookingSlot);
    }
  }, [exproShippingMode]);

  useEffect(() => {
    if (!isExproCheckout) {
      setPickUpLater(cartShippingMode.pickUpLater ?? false);
      setBookinSlot(cartShippingMode.bookingSlot);
    }
  }, [cartShippingMode]);

  const onDayChanged = (day) => {
    setSelectedDay(day);
    const hours = availableSlots.filter(dateComparator(day.value)).map(mapSlotToHour);
    hours.unshift(getEmptyOption(t('checkout:shipMode.pickUpHour')));
    setAvailableHours(hours);
  };

  const resetValues = () => {
    setSelectedDay({} as DateValueLabel);
    setSelectedHour({} as DateValueLabel);
  };

  const onHourChanged = (hour) => {
    setSelectedHour(hour);
    onChange(getSelectedSlot(hour));
  };

  return (
    <div className={`pickUp${className ? ' ' + className : ''}`}>
      {!pickUpLater && availableSlots?.length > 0 && (
        <>
          {selectedDay?.value ? (
            <p className="show-selected">
              {t('checkout:shipMode.selectedDate', {
                date: formatLongLocalDate(selectedDay.value, locale, dateOptions)
              })}
              <button className="btn btn-white" onClick={resetValues} >{t('btn.modify')}</button>
            </p>
          ) : availableDates.length > 0 && (
            <SelectDropdown id="day-selector" options={availableDates} onChange={onDayChanged} selected={selectedDay} />
          )}
          {selectedHour?.value ? (
            <p className="show-selected">
              <span>
                {t('checkout:shipMode.selectedHour', { hour: selectedHour.label })}
              </span>
              <button className="btn btn-white" onClick={() => setSelectedHour({} as DateValueLabel)}>{t('btn.modify')}</button>
            </p>
          ) : (availableHours?.length > 0 && selectedDay?.value) && (
            <SelectDropdown id="hour-selector" options={availableHours} onChange={onHourChanged} selected={selectedHour} />
          )}
        </>
      )}
      <div className="pickUpLater">
        <CheckboxV2
          className="pickUpLater-checkbox tu-later"
          label={t('checkout:shipMode.pickUpLater')}
          onChange={(e) => handlePickUpLater(e.target.checked)}
          checked={pickUpLater}
          data-test-id="pick-up-later"
        />
        {pickUpLater && (
          <p>{t('checkout:shipMode.contactNeeded')}</p>
        )}
      </div>
    </div>
  );
};

export default React.memo(DriveDateSelector);
